import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import type { UturnUserResponse } from '@uturn/api/v1';
import {
	useFetchUturnUserByIdSuspense,
	useUpdateUserDetails,
} from '@uturn/api/v1';
import { MetaDataContext } from '@uturn/portal/context';
import {
	Avatar,
	AvatarFallback,
	Button,
	Form,
	FormControl,
	FormField,
	FormFieldReadOnly,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	Separator,
	sonner,
} from '@uturn/ui-kit';
import { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formProfileSchema, parseAccountPhone } from '../schema';

export function AccountProfile() {
	const { t } = useTranslation();
	const { metadata } = useContext(MetaDataContext);

	const userId = metadata?.user?.id ?? 0;

	const { data: uturnUser } = useFetchUturnUserByIdSuspense(userId, {
		query: {
			select: (data) => data.data,
		},
	});

	const form = useForm<UturnUserResponse>({
		resolver: zodResolver(formProfileSchema),
		mode: 'onChange',
		defaultValues: uturnUser,
	});

	const name = form.watch(['firstName', 'lastName']);

	const { mutate, isPending } = useUpdateUserDetails();

	const onSubmit = (formData: UturnUserResponse) => {
		// Derive the real country code (and normalise to E.164) from the entered
		// number via the shared phone transform, instead of echoing the stored
		// default.
		const parsedPhone = parseAccountPhone(formData.phoneNumbers?.[0]?.phone);

		const data = {
			...formData,
			// Only the first phone is editable here — normalise it in place and
			// leave any additional stored phone entries untouched.
			...(parsedPhone?.success && {
				phoneNumbers: formData.phoneNumbers?.map((entry, index) =>
					index === 0 ? { ...entry, phone: parsedPhone.data } : entry,
				),
			}),
		};

		mutate(
			{
				id: userId,
				data,
			},
			{
				onSuccess: () => {
					sonner.success(
						t(
							'pages.account.profile.form.submit.success',
							'User has been updated!',
						),
					);
				},
				onError: () => {
					sonner.error(t('general.try-again-later', 'Please try again later.'));
				},
			},
		);
	};

	return (
		<>
			<Helmet
				title={t('pages.account.profile.title', 'Account - Profile').toString()}
			/>
			<div className="space-y-6">
				<div>
					<h3 className="text-lg font-medium">
						{t('pages.account.profile.title', 'Profile')}
					</h3>
					<p className="text-sm text-muted-foreground">
						{t(
							'pages.account.profile.description',
							'This is how others will see you.',
						)}
					</p>
				</div>
				<Separator />
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
						<div className="col-span-full flex items-center gap-x-8">
							<Avatar className="size-24 bg-gray-800">
								<AvatarFallback name={name.join(' ')} />
							</Avatar>
						</div>

						<div className="grid grid-flow-col gap-3">
							<FormField
								control={form.control}
								name="firstName"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="required-asterix">
											{t('general.firstname', 'First name')}
										</FormLabel>
										<FormControl>
											<Input placeholder="John" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="lastName"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="required-asterix">
											{t('general.lastname', 'Last name')}
										</FormLabel>
										<FormControl>
											<Input placeholder="Doe" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('general.email', 'Email')}</FormLabel>
									<FormControl>
										<FormFieldReadOnly value={field.value} />
									</FormControl>
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="phoneNumbers.0.phone.number"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('general.phone_number', 'Phone number')}
									</FormLabel>
									<FormControl>
										<Input placeholder="+31612345678" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<Button
							type="submit"
							disabled={isPending || !form.formState.isValid}
						>
							{isPending && (
								<Icon spin className="mr-3" icon={faSpinnerThird} />
							)}
							{t('general.actions.save', 'save')}
						</Button>
					</form>
				</Form>
			</div>
		</>
	);
}
